import {toDate} from '@/utils/dates';
import {Logger} from '@vanti/vue-logger';
import {byRef} from '@/utils/firestore';

const log = Logger.get('bookings');

/**
 * Return the key for the reservation that reserves the given resource path.
 *
 * @param {Booking} booking
 * @param {string} resourcePath
 * @return {string|null}
 */
export function reservationKey(booking, resourcePath) {
  return resourcePath;
}

export const BOOKED_FROM_PROP = 'fromTime';
export const BOOKED_TO_PROP = 'toTime';

/**
 * @param {string} resourcePath
 * @return {string[]}
 */
export function scheduleProps(resourcePath) {
  return [
    BOOKED_FROM_PROP, BOOKED_TO_PROP,
    checkInFromProp(resourcePath), checkInToProp(resourcePath)
  ];
}

/**
 * @param {string} resourcePath
 * @return {string}
 */
export function checkInProp(resourcePath) {
  return 'checkIn';
}

/**
 * @param {string} resourcePath
 * @return {string}
 */
export function checkInFromProp(resourcePath) {
  return checkInProp(resourcePath) + '.fromTime';
}

/**
 * @param {string} resourcePath
 * @return {string}
 */
export function checkInToProp(resourcePath) {
  return checkInProp(resourcePath) + '.toTime';
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
export function reservedFromProp(reservationKey) {
  return BOOKED_FROM_PROP;
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
export function reservedToProp(reservationKey) {
  return BOOKED_TO_PROP;
}

/**
 * Get the from time from the booking
 *
 * @param {Booking} b
 * @return {CanBeDate}
 */
export function fromTime(b) {
  return b.fromTime;
}

/**
 * Get the to time from the booking
 *
 * @param {Booking} b
 * @return {CanBeDate}
 */
export function toTime(b) {
  return b.toTime;
}

/**
 * @param {Booking} b
 * @param {string} resourcePath
 * @return {{fromTime: CanBeDate, toTime: CanBeDate}|undefined}
 */
export function checkInPeriod(b, resourcePath) {
  return b.checkIn;
}

/**
 * @param {Booking} b
 * @param {string} r
 * @return {boolean}
 */
export function hasCheckIn(b, r) {
  return b.hasOwnProperty('checkIn');
}

/**
 * Returns true if the booking is approved.
 *
 * @param {Booking} booking
 * @param {string} resourceId
 * @return {Object}
 */
export function approval(booking, resourceId) {
  return booking && booking.approval;
}

/**
 * @param {Booking} booking
 * @param {string} resourcePath
 * @return {boolean}
 */
export function requiresCheckIn(booking, resourcePath) {
  return booking.requiresCheckIn;
}

/**
 * Sets up a snapshot listener for bookings that reserve the given resource.
 *
 * @param {firebase.firestore.DocumentReference} resourceRef
 * @param {{startTime: CanBeDate, endTime: CanBeDate}} queryPeriod
 * @param {function(firebase.firestore.QuerySnapshot)} cb
 * @return {Object<string, function()>}
 */
export function watchBookingsForResource(resourceRef, queryPeriod, cb) {
  const bookingsRef = resourceRef.parent.parent.collection('bookings');

  const spaceQuery = bookingsRef
      .where('space.ref', '==', resourceRef)
      .where('fromTime', '>=', toDate(queryPeriod.startTime))
      .where('fromTime', '<', toDate(queryPeriod.endTime));
  const spacesQuery = bookingsRef
      .where('spaces_ref', 'array-contains', resourceRef)
      .where('fromTime', '>=', toDate(queryPeriod.startTime))
      .where('fromTime', '<', toDate(queryPeriod.endTime));
  const stopFns = [
    spaceQuery.onSnapshot(
        snap => cb(snap),
        err => log.error(`watchBookingsForResource ${resourceRef.path}.space`, err)),
    spacesQuery.onSnapshot(
        snap => cb(snap),
        err => log.error(`watchBookingsForResource ${resourceRef.path}.spaces`, err))
  ];
  return {
    [resourceRef.path]: () => stopFns.forEach(fn => fn())
  };
}

/**
 * Create a new ad hoc booking document.
 *
 * @param {Date|firebase.firestore.Timestamp} fromTime
 * @param {Date|firebase.firestore.Timestamp} toTime
 * @param {Object} resource
 * @param {kahu.firestore.Creatable} [creatableSnippet]
 * @return {Booking}
 */
export function newAdHocBooking(fromTime, toTime, resource, creatableSnippet) {
  const creatable = creatableSnippet || {};
  return /** @type {Booking} */ {
    ...creatable,
    title: 'Ad Hoc Meeting',
    fromTime,
    toTime,
    space: resource,
    requiresCheckIn: false
  };
}

/**
 * @param {Booking} booking
 * @return {number}
 */
export function connectionCount(booking) {
  return booking.connectionCount || 0;
}

/**
 * @param {Booking} booking
 * @return {string}
 */
export function connectionRefKey(booking) {
  return byRef(booking.connection);
}

/**
 * @param {Booking} booking
 * @param {firebase.firestore.DocumentReference} resourceRef
 * @return {boolean}
 */
export function bookingReservesResource(booking, resourceRef) {
  return booking && booking.space && booking.space.ref && booking.space.ref.isEqual(resourceRef);
}

/**
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {DocumentSnippet|null}
 */
export function bookingReservedResource(booking, reservationKey) {
  return booking && booking.space;
}
